import { useEffect, useState } from 'react';
import { Link, Route, Routes, useLocation } from 'react-router-dom';
import { MARKETING_URLS } from '@/app/constants';
import App from './App';

/** The legal documents' public home. The app's own paths for them go there,
 *  signed in or out, as the waitlist's do (JAR-2459). */
const LEGAL_DOCUMENTS: Record<string, { url: string; name: string }> = {
  '/terms': { url: MARKETING_URLS.terms, name: 'Terms of Service' },
  '/privacy': { url: MARKETING_URLS.privacy, name: 'Privacy Policy' },
  '/cookies': { url: MARKETING_URLS.cookies, name: 'Cookie Policy' },
};

/** Leaves the app for a page outside it, keeping the path's query and section,
 *  and replacing this entry in history so Back doesn't land on the redirect
 *  again. Offline, a Home Screen app would hand the browser a page it can't
 *  load and leave the person on its error, so this waits for the connection
 *  instead, and waits again if it drops before the browser leaves. The way back
 *  into the app shows while the page opens too, for a slow connection. A page
 *  that fails to load online still ends on the browser's error (JAR-2656). */
function LeaveFor({ url, name }: { url: string; name: string }) {
  const { search, hash } = useLocation();
  const [online, setOnline] = useState(() => navigator.onLine);

  useEffect(() => {
    const backOnline = () => setOnline(true);
    const wentOffline = () => setOnline(false);
    window.addEventListener('online', backOnline);
    window.addEventListener('offline', wentOffline);
    return () => {
      window.removeEventListener('online', backOnline);
      window.removeEventListener('offline', wentOffline);
    };
  }, []);

  useEffect(() => {
    if (online) window.location.replace(url + search + hash);
  }, [online, url, search, hash]);

  // The app's loading idiom (App.tsx's appFallback), so the page isn't blank
  // while the browser leaves.
  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center text-muted" role="status" aria-live="polite">
      <p>{online ? `Opening the ${name}…` : `You're offline. We'll open the ${name} when you're back online.`}</p>
      <Link to="/" className="font-medium text-accent hover:opacity-80">Back to JarvisTravel</Link>
    </div>
  );
}

/** The legal documents' paths come before the app, so they don't wait on its
 *  sign-in. Any other path is the app: one it doesn't know goes to sign-in, as
 *  an app behind sign-in does (JAR-2459's decision). */
export function AppRoutes() {
  return (
    <Routes>
      {Object.entries(LEGAL_DOCUMENTS).map(([path, doc]) => (
        <Route key={path} path={path} element={<LeaveFor {...doc} />} />
      ))}
      <Route path="/*" element={<App />} />
    </Routes>
  );
}
